import { createApi } from '@reduxjs/toolkit/query/react';
import { type NetworkStatus, type ReferentPriceType } from '@strata/constants';
import { formatNameLastFirst } from '@strata/helper-functions';

import { APERTURE_API_URL } from 'api/APIBase';
import { handleRTKQueryError } from 'services/Error';
import { getBaseQuery } from 'store/api/apiBaseQuery';

export interface MedicationStats {
    value?: string;
    numberOfReferrals: number;
    baselineRate?: string;
    avgWinningBidRate?: string;
    referentPriceType: ReferentPriceType;
}

export interface MedicationSummary {
    productServiceOid: string;
    drugName: string;
    isBrand: boolean;
    therapeuticCategory?: string;
    numberOfReferrals: number;
    baselineRate?: string;
    avgWinningBidRate?: string;
    referentPriceType: ReferentPriceType;
}

export interface MedicationDetails {
    productServiceOid: string;
    drugName: string;
    isBrand: boolean;
    numberOfReferrals: number;
    baselineRate?: string;
    avgWinningBidRate?: string;
    referentPriceType: ReferentPriceType;
    stats: {
        key: string;
        values?: MedicationStats[];
    }[];
}

export interface PharmacySummary {
    npi: string;
    name: string;
    networkStatus: NetworkStatus;
    referrals: number;
    winningPct: string;
}

export interface ServicingPharmacyForProduct {
    name: string;
    referrals: number;
    npi: string;
    networkStatus: NetworkStatus;
}

export interface PrescriberSummary {
    npi: string;
    name: string;
    topDrugName: string;
    referrals: number;
    specialization?: string;
}

export const apertureApi = createApi({
    baseQuery: getBaseQuery(APERTURE_API_URL),
    reducerPath: 'apertureApi',
    endpoints: (build) => ({
        /** Tests connection to data lake */
        testLakeConnection: build.query<void, void>({ query: () => 'prescribers/lake-test' }),
        /** Gets medications summary stats */
        getMedications: build.query<MedicationSummary[], { segmentId?: string }>({
            query: ({ segmentId }) => ({
                url: 'medications',
                params: { segment_id: segmentId },
            }),
            transformResponse: (
                response: {
                    product_service_oid: string;
                    drug_name: string;
                    is_brand: boolean;
                    number_of_referrals: number;
                    baseline_rate?: string;
                    avg_winning_bid_rate?: string;
                    referent_price_type: ReferentPriceType;
                    therapeutic_category?: string;
                }[],
            ) =>
                response?.map((product) => ({
                    productServiceOid: product.product_service_oid,
                    drugName: product.drug_name,
                    isBrand: product.is_brand,
                    numberOfReferrals: product.number_of_referrals,
                    baselineRate: product.baseline_rate,
                    avgWinningBidRate: product.avg_winning_bid_rate,
                    referentPriceType: product.referent_price_type,
                    therapeuticCategory: product.therapeutic_category,
                })),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving products.'),
        }),
        /** Gets medication detail stats */
        getMedicationDetails: build.query<
            MedicationDetails,
            {
                segmentId?: string;
                productServiceOid: string;
                isBrand: boolean;
                referentPriceType: ReferentPriceType;
            }
        >({
            query: ({ segmentId, productServiceOid, isBrand, referentPriceType }) => ({
                url: `medications/${productServiceOid}`,
                params: {
                    segment_id: segmentId,
                    is_brand: isBrand,
                    referent_price_type: referentPriceType,
                },
            }),
            transformResponse: (
                response: {
                    product_service_oid: string;
                    drug_name: string;
                    is_brand: boolean;
                    number_of_referrals: number;
                    baseline_rate?: string;
                    avg_winning_bid_rate?: string;
                    referent_price_type: ReferentPriceType;
                    stats: {
                        title: string;
                        values?: {
                            value?: string;
                            number_of_referrals: number;
                            baseline_rate?: string;
                            avg_winning_bid_rate?: string;
                            referent_price_type: ReferentPriceType;
                        }[];
                    }[];
                }[],
            ) => {
                const product = response?.[0];
                return {
                    productServiceOid: product?.product_service_oid,
                    drugName: product?.drug_name,
                    isBrand: product?.is_brand,
                    numberOfReferrals: product?.number_of_referrals,
                    baselineRate: product?.baseline_rate,
                    avgWinningBidRate: product?.avg_winning_bid_rate,
                    referentPriceType: product?.referent_price_type,
                    stats: product?.stats?.map((stat) => ({
                        key: stat.title,
                        values: stat.values?.map((val) => ({
                            value: val.value?.trim(),
                            numberOfReferrals: val.number_of_referrals,
                            baselineRate: val.baseline_rate,
                            avgWinningBidRate: val.avg_winning_bid_rate,
                            referentPriceType: val.referent_price_type,
                        })),
                    })),
                };
            },
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving product details.',
            ),
        }),
        getServicingPharmaciesForProduct: build.query<
            ServicingPharmacyForProduct[],
            {
                segmentId?: string;
                productServiceOid: string;
                isBrand: boolean;
                referentPriceType: ReferentPriceType;
            }
        >({
            query: ({ segmentId, productServiceOid, isBrand, referentPriceType }) => ({
                url: `medications/bypharmacy/${productServiceOid}`,
                params: {
                    segment_id: segmentId,
                    is_brand: isBrand,
                    referent_price_type: referentPriceType,
                },
            }),
            transformResponse: (
                response: {
                    pharmacy_npi: string;
                    organization_name: string;
                    number_of_referrals: number;
                    network_status: NetworkStatus;
                }[],
            ) =>
                response?.map((pharmacy) => ({
                    name: pharmacy.organization_name,
                    referrals: pharmacy.number_of_referrals,
                    npi: pharmacy.pharmacy_npi,
                    networkStatus: pharmacy.network_status,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving servicing pharmacies.',
            ),
        }),
        /** Gets pharmacies summary stats */
        getPharmacies: build.query<PharmacySummary[], { segmentId?: string }>({
            query: ({ segmentId }) => ({
                url: 'pharmacies',
                params: { segment_id: segmentId },
            }),
            transformResponse: (
                response: {
                    pharmacy_npi: string;
                    organization_name: string;
                    network_status: NetworkStatus;
                    number_of_referrals: number;
                    referral_win_percentage: string;
                }[],
            ) =>
                response?.map((pharmacy) => ({
                    npi: pharmacy.pharmacy_npi,
                    name: pharmacy.organization_name,
                    networkStatus: pharmacy.network_status,
                    referrals: pharmacy.number_of_referrals,
                    winningPct: pharmacy.referral_win_percentage,
                })),
            transformErrorResponse: handleRTKQueryError('An error occurred retrieving pharmacies.'),
        }),
        /** Gets prescribers summary stats */
        getPrescribers: build.query<PrescriberSummary[], { segmentId?: string }>({
            query: ({ segmentId }) => ({
                url: 'prescribers',
                params: { segment_id: segmentId },
            }),
            transformResponse: (
                response: {
                    prescriber_npi: string;
                    first_name: string;
                    last_name: string;
                    specialization?: string;
                    top_drug_name: string;
                    number_of_referrals: number;
                }[],
            ) =>
                response?.map((prescriber) => ({
                    npi: prescriber.prescriber_npi,
                    name: formatNameLastFirst(prescriber.last_name, prescriber.first_name),
                    topDrugName: prescriber.top_drug_name,
                    referrals: prescriber.number_of_referrals,
                    specialization: prescriber.specialization,
                })),
            transformErrorResponse: handleRTKQueryError(
                'An error occurred retrieving prescribers.',
            ),
        }),
    }),
});

export const {
    useGetMedicationsQuery,
    useGetMedicationDetailsQuery,
    useGetServicingPharmaciesForProductQuery,
    useGetPharmaciesQuery,
    useGetPrescribersQuery,
    useTestLakeConnectionQuery,
} = apertureApi;
